import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useAdminOrderDetail,
  useCompleteOrder,
  useCancelOrder,
} from "@/hooks/useAdminOrders";
import type { OrderUpgradeNotApplied } from "@/services/admin-order.service";
import { useResourceDetail } from "@/hooks/useResourceQueries";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { usePermissions } from "@/hooks/usePermissions";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { PageHeader } from "@/components/common/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime, formatDate } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Download,
  Building2,
  CreditCard,
  FileText,
  Copy,
  Check,
  Shield,
  Network,
  Calendar,
  Info,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { CreateEInvoiceRequestDialog } from "@/components/e-invoices/CreateEInvoiceRequestDialog";
import { BusyOverlay } from "@/components/common/BusyOverlay";

export function AdminOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const {
    data: order,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAdminOrderDetail(orderId!);
  const { hasRole } = usePermissions();
  const canSettleOrder = hasRole(["SuperAdmin", "BillingAdmin"]);
  const completeOrderMutation = useCompleteOrder();
  const cancelOrderMutation = useCancelOrder();

  // Dialog states
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [createEInvoiceDialogOpen, setCreateEInvoiceDialogOpen] =
    useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [completePaymentId, setCompletePaymentId] = useState("");
  const [completeInvoiceId, setCompleteInvoiceId] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  // VM detail state & query
  const [selectedVmId, setSelectedVmId] = useState<string | null>(null);
  const [vmModalOpen, setVmModalOpen] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const {
    data: vmDetail,
    isLoading: isLoadingVm,
    error: vmError,
    refetch: refetchVm,
  } = useResourceDetail(selectedVmId!);

  const handleCopyText = (text: string, type: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedText(type);
      setTimeout(() => setCopiedText(null), 2000);
    });
  };

  const openVmDetails = (resourceId?: string) => {
    if (!resourceId) return;
    setSelectedVmId(resourceId);
    setVmModalOpen(true);
  };

  const handleCompleteOrder = () => {
    if (!order || !completePaymentId || !completeInvoiceId) return;

    completeOrderMutation.mutate(
      {
        orderId: order.id,
        paymentId: completePaymentId,
        invoiceId: completeInvoiceId,
      },
      {
        // No extra `refetch()` here: the mutation's own `onSettled` already invalidates
        // `["admin-order", id]`, and the mutation stays `isPending` until that refetch has
        // landed, so this callback only needs to close the dialog.
        onSuccess: () => {
          setCompleteDialogOpen(false);
        },
      },
    );
  };

  const handleCancelOrder = () => {
    if (!order || !cancelReason) return;

    cancelOrderMutation.mutate(
      {
        orderId: order.id,
        reason: cancelReason,
      },
      {
        onSuccess: () => {
          setCancelDialogOpen(false);
        },
      },
    );
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Draft: "bg-secondary text-muted-foreground",
      Pending: "bg-warn-soft text-warn-ink",
      Paid: "bg-info-soft text-info-ink",
      Processing: "bg-brand-soft text-brand-ink",
      Complete: "bg-ok-soft text-ok-ink",
      Failed: "bg-destructive-soft text-destructive-ink",
      Cancelled: "bg-secondary text-muted-foreground",
    };

    return <Badge className={colors[status] || ""}>{status}</Badge>;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error && !order) {
    // Retry before the way out: a failed request here is usually transient, and walking back to
    // the list to find the same order again is a worse first option than trying again in place.
    // Gated on `!order` too: every write on this page now awaits a detail refetch (including its
    // own error path — see `useCompleteOrder`/`useCancelOrder`'s `onSettled`), and a refetch that
    // fails must not blow away an already loaded order and its open dialogs.
    return (
      <div className="space-y-4">
        <ErrorFallback error={error as Error} onRetry={() => refetch()} />
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate("/admin/orders")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-muted-foreground">Order not found</p>
        <Button variant="outline" onClick={() => navigate("/admin/orders")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>
      </div>
    );
  }

  // Completing or cancelling an order is SuperAdmin+BillingAdmin; the page itself reads fine for all.
  const canComplete =
    canSettleOrder &&
    (order.status === "Paid" || order.status === "Processing");
  // Matches layer 1 of the backend guard (AdminCancelOrderCommandHandler), which refuses anything
  // past Pending with a 409. The old condition offered the button on Paid and Processing orders
  // too, where the money has moved or a machine is being built — the operator would click it and
  // get a conflict back. Layers 2 and 3 (a confirmed payment, a machine already created) are not
  // visible from this payload, so the backend still has the final say and its message is what the
  // dialog shows.
  const canCancel =
    canSettleOrder && (order.status === "Draft" || order.status === "Pending");
  const vmItems = order.items.filter(
    (item) => item.resourceType === "VirtualMachine",
  );

  // A wallet top-up order carries no OrderItem at all: OrderItem.ProductId is a required FK into
  // products, so there is no catalogue row for money paid into the balance to point at. Every
  // screen keying on an order's items therefore needs its own branch for this type.
  const isBalanceTopUp = order.type === "BalanceTopUp";
  // An upgrade line spells out what is bought (disk, vCPU, RAM), so it wraps instead of truncating.
  const isUpgrade = order.type === "Upgrade";
  const notApplied = order.items.flatMap((item) => item.notApplied ?? []);
  const isNotFullyApplied =
    order.status === "Complete" && notApplied.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order ${order.orderNumber}`}
        subtitle="Order details and management"
        back={{
          label: "Back to Orders",
          onClick: () => navigate("/admin/orders"),
        }}
        actions={[
          ...(canSettleOrder &&
          order.status === "Complete" &&
          order.type !== "BalanceTopUp"
            ? [
                {
                  label: "Create E-Invoice Request",
                  onClick: () => setCreateEInvoiceDialogOpen(true),
                  variant: "outline" as const,
                  icon: <FileText className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
          ...(canComplete
            ? [
                {
                  label: "Complete Order",
                  onClick: () => {
                    setCompletePaymentId(order.paymentId || "");
                    setCompleteInvoiceId(order.invoiceId || "");
                    setCompleteDialogOpen(true);
                  },
                  // Neither mutation's own dialog can be open while the other is out (only one
                  // dialog state opens at a time), but disabling both while either is pending
                  // keeps a second write from starting from the header while the page overlay
                  // is up for the first.
                  disabled:
                    completeOrderMutation.isPending ||
                    cancelOrderMutation.isPending,
                  icon: <CheckCircle2 className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
          ...(canCancel
            ? [
                {
                  label: "Cancel Order",
                  onClick: () => {
                    setCancelReason("");
                    setCancelDialogOpen(true);
                  },
                  variant: "destructive" as const,
                  disabled:
                    completeOrderMutation.isPending ||
                    cancelOrderMutation.isPending,
                  icon: <XCircle className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
          ...(order.invoiceId
            ? [
                {
                  label: "Download Invoice",
                  onClick: () => {},
                  variant: "outline" as const,
                  disabled: true,
                  icon: <Download className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
        ]}
        action={
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
            />
          </Button>
        }
      />

      {/* A refetch failed but the last good read (`order`) is still on screen — report it here
          rather than tearing the whole page down to the full-screen ErrorFallback, which would
          unmount the open Complete/Cancel dialog mid-request along with everything else below. */}
      {error && (
        <div className="flex items-center justify-between gap-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <span className="text-destructive-ink">
            Could not refresh this order. Showing the last data loaded.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Retry
          </Button>
        </div>
      )}

      {isNotFullyApplied && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Upgrade not fully applied</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              The customer paid and the configuration is recorded, but the
              infrastructure did not apply these parts:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              {notApplied.map((part, index) => (
                <li key={index}>{describeNotApplied(part)}</li>
              ))}
            </ul>
            <p>
              A call that got no answer may still have landed, so check the
              machine in n8n before applying a part by hand. n8n's answer is in
              the audit log, action resource.upgrade_not_applied.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Order Overview */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Order Information */}
        <Card>
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Order Number</p>
                <p className="font-medium">{order.orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="mt-1">
                  {isNotFullyApplied ? (
                    <Badge className="bg-warn-soft text-warn-ink">
                      Not fully applied
                    </Badge>
                  ) : (
                    getStatusBadge(order.status)
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-medium">{order.type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Items</p>
                <p className="font-medium">{order.itemsCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Placed At</p>
                <p className="font-medium">{formatDateTime(order.placedAt)}</p>
              </div>
              {order.completedAt && (
                <div>
                  <p className="text-sm text-muted-foreground">Completed At</p>
                  <p className="font-medium">
                    {formatDateTime(order.completedAt)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tenant & User Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Tenant & User
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Tenant</p>
              <Button
                variant="link"
                className="p-0 h-auto font-medium"
                onClick={() => navigate(`/admin/tenants/${order.tenantId}`)}
              >
                {order.tenantName}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                ID: {order.tenantId}
              </p>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-muted-foreground">User</p>
              <p className="font-medium">{order.userEmail}</p>
              <p className="text-xs text-muted-foreground mt-1">
                ID: {order.userId}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Items. A top-up has none, and falling through to the VM table would put
          "Order này không có máy ảo." on a correctly-formed order — reading as a broken one. */}
      {isBalanceTopUp ? (
        <Card>
          <CardHeader>
            <CardTitle>Nội dung đơn</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Nạp số dư vào ví</p>
                <p className="text-sm text-muted-foreground">
                  Đơn này không cấp phát tài nguyên. Tiền vào số dư của tenant
                  khi thanh toán được xác nhận.
                </p>
              </div>
              <p className="text-lg font-semibold">
                {formatCurrency(order.total, order.currency)}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Danh sách máy ảo</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sản phẩm</TableHead>
                  <TableHead>Tên VM</TableHead>
                  <TableHead>IP Public</TableHead>
                  <TableHead>Trạng thái VM</TableHead>
                  <TableHead>Loại tài nguyên</TableHead>
                  <TableHead>Số lượng</TableHead>
                  <TableHead>Đơn giá</TableHead>
                  <TableHead>Thành tiền</TableHead>
                  <TableHead className="text-right">Hành động</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vmItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="h-24 text-center text-muted-foreground"
                    >
                      Order này không có máy ảo.
                    </TableCell>
                  </TableRow>
                ) : (
                  vmItems.map((item) => {
                    const hasResource =
                      item.resourceAvailable && !!item.resourceId;
                    const resourceWasRemoved =
                      !item.resourceAvailable && !!item.resourceId;

                    const getVmStatusBadge = (status: string) => {
                      const colors: Record<string, string> = {
                        Running:
                          "bg-ok-soft text-ok-ink border-ok/20 hover:bg-ok-soft",
                        Stopped:
                          "bg-secondary text-muted-foreground border-secondary/20 hover:bg-secondary",
                        Suspended:
                          "bg-warn-soft text-warn-ink border-warn/20 hover:bg-warn-soft",
                        Error:
                          "bg-destructive-soft text-destructive-ink border-destructive/20 hover:bg-destructive-soft",
                        Starting:
                          "bg-brand-soft text-brand-ink border-brand/20 hover:bg-brand-soft",
                        Stopping:
                          "bg-brand-soft text-brand-ink border-brand/20 hover:bg-brand-soft",
                        Pending:
                          "bg-warn-soft text-warn-ink border-warn/20 hover:bg-warn-soft",
                      };

                      return (
                        <Badge
                          variant="outline"
                          className={colors[status] || ""}
                        >
                          {status}
                        </Badge>
                      );
                    };

                    return (
                      <TableRow
                        key={item.id}
                        className={
                          hasResource ? "cursor-pointer hover:bg-muted/50" : ""
                        }
                        tabIndex={hasResource ? 0 : undefined}
                        onClick={() => {
                          if (hasResource) {
                            openVmDetails(item.resourceId);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (
                            hasResource &&
                            (event.key === "Enter" || event.key === " ")
                          ) {
                            event.preventDefault();
                            openVmDetails(item.resourceId);
                          }
                        }}
                      >
                        <TableCell>
                          <div className={isUpgrade ? "max-w-72" : "max-w-52"}>
                            <p
                              className={
                                isUpgrade
                                  ? "break-words font-medium"
                                  : "truncate font-medium"
                              }
                              title={item.description}
                            >
                              {item.description}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {item.productName} — {item.billingFrequency}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {item.resourceName ? (
                            // Capped so a long VM name truncates instead of pushing the
                            // other columns off screen; the full name is in the tooltip.
                            <div
                              className="max-w-48 truncate"
                              title={item.resourceName}
                            >
                              {item.resourceName}
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic text-xs">
                              {resourceWasRemoved
                                ? "Không còn tồn tại"
                                : "Chờ khởi tạo"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{item.publicIpAddress || "-"}</TableCell>
                        <TableCell>
                          {item.resourceStatus ? (
                            getVmStatusBadge(item.resourceStatus)
                          ) : (
                            <span className="text-muted-foreground italic text-xs">
                              {resourceWasRemoved ? "Đã chấm dứt" : "N/A"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{item.resourceType}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          {formatCurrency(item.unitPrice, order.currency)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(item.totalPrice, order.currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          {hasResource ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                openVmDetails(item.resourceId);
                              }}
                            >
                              Xem thông tin VM
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              -
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">
                {formatCurrency(order.subtotal, order.currency)}
              </span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-ok-ink">
                <span>
                  Discount {order.discountCode && `(${order.discountCode})`}
                </span>
                <span>-{formatCurrency(order.discount, order.currency)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Tax ({order.taxRate}%)
              </span>
              <span className="font-medium">
                {formatCurrency(order.tax, order.currency)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>{formatCurrency(order.total, order.currency)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Information */}
      {order.payment && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Payment ID</p>
                <Button
                  variant="link"
                  className="p-0 h-auto font-medium"
                  onClick={() =>
                    navigate(`/admin/payments/${order.payment!.id}`)
                  }
                >
                  {order.payment.id}
                </Button>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Order Number</p>
                <p className="font-medium">{order.payment.orderNumber}</p>
              </div>
              {/* The two codes an operator needs to find this payment in T-PayGate: ours, sent
                  as RefTransactionId, and the bill code the gateway issued for the QR. */}
              <div>
                <p className="text-sm text-muted-foreground">Payment Code</p>
                <div className="flex items-center gap-1">
                  <p className="font-mono text-sm font-medium">
                    {order.payment.paymentCode}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground"
                    title="Copy payment code"
                    onClick={() =>
                      handleCopyText(order.payment!.paymentCode, "paymentCode")
                    }
                  >
                    {copiedText === "paymentCode" ? (
                      <Check className="h-3.5 w-3.5 text-ok-ink" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
              {order.payment.tPayGateBillCode && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    T-PayGate Bill Code
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="font-mono text-sm font-medium">
                      {order.payment.tPayGateBillCode}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground"
                      title="Copy bill code"
                      onClick={() =>
                        handleCopyText(
                          order.payment!.tPayGateBillCode!,
                          "billCode",
                        )
                      }
                    >
                      {copiedText === "billCode" ? (
                        <Check className="h-3.5 w-3.5 text-ok-ink" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Method</p>
                <p className="font-medium">{order.payment.method}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium">{order.payment.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="font-medium">
                  {formatCurrency(order.payment.amount, order.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created At</p>
                <p className="font-medium">
                  {formatDateTime(order.payment.createdAt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice Information */}
      {order.invoice && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Invoice ID</p>
                <Button
                  variant="link"
                  className="p-0 h-auto font-medium"
                  onClick={() =>
                    navigate(`/admin/invoices/${order.invoice!.id}`)
                  }
                >
                  {order.invoice.invoiceNumber}
                </Button>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium">{order.invoice.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Issue Date</p>
                <p className="font-medium">
                  {formatDateTime(order.invoice.issueDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Date</p>
                <p className="font-medium">
                  {formatDateTime(order.invoice.dueDate)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Notes */}
      {order.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Admin Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Cancellation / failure reason. A Failed order carries the same field — the operator's
          own text from the provisioning failure path (`Failed` is now written only when a paid
          order's provisioning fails) — but never a `cancelledAt` timestamp, so that row is shown
          only when there is one to show. */}
      {(order.status === "Cancelled" || order.status === "Failed") &&
        order.cancellationReason && (
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="text-destructive-ink">
                {order.status === "Cancelled"
                  ? "Cancellation Reason"
                  : "Failure Reason"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {order.cancelledAt && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Cancelled At
                    </p>
                    <p className="font-medium">
                      {formatDateTime(order.cancelledAt)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <p className="text-sm whitespace-pre-wrap">
                    {order.cancellationReason}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Complete Order Dialog */}
      <Dialog
        open={completeDialogOpen}
        onOpenChange={(next) => {
          // The mutation stays open until its onSettled invalidation lands (see
          // handleCompleteOrder), so a request in flight owns the dialog.
          if (completeOrderMutation.isPending) return;
          setCompleteDialogOpen(next);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(event) => {
            if (completeOrderMutation.isPending) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Complete Order</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Manually mark order {order.orderNumber} as complete.
            </DialogDescription>
            <div className="space-y-2">
              <Label htmlFor="paymentId">Payment ID</Label>
              <Input
                id="paymentId"
                placeholder="Enter payment ID"
                value={completePaymentId}
                onChange={(e) => setCompletePaymentId(e.target.value)}
                disabled={completeOrderMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceId">Invoice ID</Label>
              <Input
                id="invoiceId"
                placeholder="Enter invoice ID"
                value={completeInvoiceId}
                onChange={(e) => setCompleteInvoiceId(e.target.value)}
                disabled={completeOrderMutation.isPending}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompleteDialogOpen(false)}
              disabled={completeOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompleteOrder}
              disabled={
                !completePaymentId ||
                !completeInvoiceId ||
                completeOrderMutation.isPending
              }
            >
              {completeOrderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {completeOrderMutation.isPending
                ? "Completing..."
                : "Complete Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(next) => {
          if (cancelOrderMutation.isPending) return;
          setCancelDialogOpen(next);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(event) => {
            if (cancelOrderMutation.isPending) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Cancel order {order.orderNumber}. Its open payment is cancelled,
              any reserved machines are released, and the invoice is voided — no
              money is refunded. The reason is shown to the customer.
            </DialogDescription>
            <div className="space-y-2">
              <Label htmlFor="reason">Cancellation Reason</Label>
              <Textarea
                id="reason"
                placeholder="Enter reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                disabled={cancelOrderMutation.isPending}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={!cancelReason || cancelOrderMutation.isPending}
            >
              {cancelOrderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {cancelOrderMutation.isPending ? "Cancelling..." : "Cancel Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BusyOverlay
        open={completeOrderMutation.isPending}
        label="Completing order…"
      />
      <BusyOverlay
        open={cancelOrderMutation.isPending}
        label="Cancelling order…"
      />

      {/* VM Detail Modal (Thông tin hệ thống) */}
      <Dialog open={vmModalOpen} onOpenChange={setVmModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Thông tin hệ thống VM</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              Chi tiết thông số kỹ thuật và trạng thái của máy ảo đã cấp phát.
            </DialogDescription>
            {isLoadingVm ? (
              <div className="py-12 flex justify-center items-center">
                <LoadingSpinner />
              </div>
            ) : vmError ? (
              // Distinct from "resource not found" below: this is a failed request, and the fix is
              // to try it again, not to accept that the VM is gone.
              <div className="py-12 flex flex-col items-center gap-3 text-center text-muted-foreground">
                <p>Could not load this VM's details.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchVm()}
                  disabled={isLoadingVm}
                >
                  Retry
                </Button>
              </div>
            ) : !vmDetail ? (
              <div className="py-12 text-center text-muted-foreground">
                This resource no longer exists.
              </div>
            ) : (
              <div className="space-y-6">
                {/* VM Main Header Banner */}
                <div className="p-6 rounded-lg bg-brand-soft border border-brand/10 flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-2xl font-bold text-foreground flex items-center gap-2">
                      {vmDetail.name}
                    </h3>
                    {/* ink-2, not muted: the brand-soft banner tint lifts the background
                      to #f9e0eb, where muted text falls to 4.25:1 — just under WCAG AA.
                      This is the only spot in the console where muted text sits on a
                      brand tint. */}
                    <p className="text-sm text-ink-2 flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-medium uppercase border border-border">
                        Server
                      </span>
                      {vmDetail.vm?.operatingSystem || "-"}
                    </p>
                  </div>
                  <div>
                    {vmDetail.status && (
                      <Badge
                        className={
                          vmDetail.status === "Running"
                            ? "bg-ok-soft text-ok-ink border-ok/20 hover:bg-ok-soft"
                            : vmDetail.status === "Stopped"
                              ? "bg-secondary text-muted-foreground border-secondary/20 hover:bg-secondary"
                              : "bg-warn-soft text-warn-ink border-warn/20 hover:bg-warn-soft"
                        }
                      >
                        {vmDetail.status}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Detail Sections Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Section 1: Định danh & Cấu hình */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Thông tin định danh
                      </h4>
                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Hostname / Tên VM:
                          </span>
                          <span className="font-semibold text-right">
                            {vmDetail.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            ID máy ảo (UUID):
                          </span>
                          <span
                            className="font-mono text-xs text-muted-foreground max-w-[180px] truncate text-right"
                            title={vmDetail.vm?.uuid || undefined}
                          >
                            {vmDetail.vm?.uuid || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Trạng thái VM:
                          </span>
                          <span className="font-medium text-right">
                            {vmDetail.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Cấu hình phần cứng
                      </h4>
                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Hệ điều hành:
                          </span>
                          <span className="font-medium text-right">
                            {vmDetail.vm?.operatingSystem || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">vCPU:</span>
                          <span className="font-semibold text-right">
                            {vmDetail.vm?.vcpu != null
                              ? `${vmDetail.vm.vcpu} vCPU`
                              : "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Bộ nhớ (RAM):
                          </span>
                          <span className="font-semibold text-right">
                            {vmDetail.vm?.memoryMb
                              ? vmDetail.vm.memoryMb >= 1024
                                ? `${Math.round(vmDetail.vm.memoryMb / 1024)} GB`
                                : `${vmDetail.vm.memoryMb} MB`
                              : "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Ổ đĩa (dung lượng + loại):
                          </span>
                          <span className="font-semibold text-right">
                            {vmDetail.vm?.diskSizeGb != null
                              ? `${vmDetail.vm.diskSizeGb} GB${
                                  vmDetail.vm.diskTypes.length
                                    ? ` (${vmDetail.vm.diskTypes.join(", ")})`
                                    : ""
                                }`
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Mạng & Dịch vụ */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <Network className="h-3 w-3" />
                        Mạng kết nối
                      </h4>
                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            IP Public:
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-semibold text-brand">
                              {vmDetail.vm?.publicIpAddress || "-"}
                            </span>
                            {vmDetail.vm?.publicIpAddress && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground"
                                onClick={() =>
                                  handleCopyText(
                                    vmDetail.vm!.publicIpAddress!,
                                    "publicIp",
                                  )
                                }
                              >
                                {copiedText === "publicIp" ? (
                                  <Check className="h-3.5 w-3.5 text-ok-ink" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            IP Private:
                          </span>
                          <span className="font-mono text-muted-foreground text-right">
                            {vmDetail.vm?.privateIpAddress || "-"}
                          </span>
                        </div>
                        {!!vmDetail.vm?.vlans.length && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              VLAN / Subnet:
                            </span>
                            <span className="font-mono text-muted-foreground text-right">
                              {vmDetail.vm.vlans.join(", ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Thông tin dịch vụ & Liên kết
                      </h4>
                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Gói dịch vụ:
                          </span>
                          <span className="font-medium text-right">
                            {vmDetail.product?.name || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Chu kỳ thanh toán:
                          </span>
                          <span className="font-medium text-right">
                            {order.items.find(
                              (i) => i.resourceId === vmDetail.id,
                            )?.billingFrequency || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Ngày tạo:
                          </span>
                          <span className="font-medium text-right">
                            {formatDate(vmDetail.createdAt)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Ngày hết hạn:
                          </span>
                          {/* No expiry means no paid term yet, never "unlimited": the backend
                            sets it only when the order is paid and the billing cycle opens
                            (Resource.AssignBillingCycle), so a VM on a Draft order has none. */}
                          <span
                            className={
                              vmDetail.expiresAt
                                ? "font-medium text-brand text-right"
                                : "font-medium text-muted-foreground text-right"
                            }
                          >
                            {vmDetail.expiresAt
                              ? formatDate(vmDetail.expiresAt)
                              : "Chưa có"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Order liên kết:
                          </span>
                          <span className="font-mono text-muted-foreground text-right">
                            #{order.orderNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogBody>

          <DialogFooter className="mt-4 border-t border-border pt-4">
            <Button onClick={() => setVmModalOpen(false)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {createEInvoiceDialogOpen && (
        <CreateEInvoiceRequestDialog
          open={createEInvoiceDialogOpen}
          onOpenChange={setCreateEInvoiceDialogOpen}
          order={order}
        />
      )}
    </div>
  );
}

/** One part of an upgrade that did not land, in an operator's words. */
function describeNotApplied(part: OrderUpgradeNotApplied): string {
  if (part.kind === "newDisk") {
    return `New disk: ${part.sizeGb ?? 0} GB`;
  }

  if (part.kind === "disk") {
    return `Disk ${part.device ?? ""}: +${part.sizeGb ?? 0} GB`;
  }

  const targets: string[] = [];
  if (part.cores) {
    targets.push(`${part.cores} vCPU`);
  }
  if (part.memoryMb) {
    targets.push(`${part.memoryMb / 1024} GB RAM`);
  }
  return `CPU/RAM: to ${targets.join(", ")}`;
}
